import type { D2DTag } from '@/types/d2d';

/**
 * ========================================================================
 * ZENTRALE D2D TAG REGELN - NICHT ÄNDERN!!!
 * ========================================================================
 * 
 * Diese Regeln definieren die Tag-Kompatibilität und dürfen NICHT geändert werden.
 * Jede Änderung kann das gesamte Follow-Up-System beeinträchtigen.
 * 
 * HAUPTREGELN:
 * ------------
 * 1. BK und HOT: Universal - können mit ALLEN Tags koexistieren
 * 
   * 2. KT (Kontakt/Termin):
   *    - Kann mit: BK, HOT
   *    - Entfernt: NA, KI, NO
   *    - Aktiviert automatisch: HOT
   *    - Bedeutung: Es gibt einen konkreten Termin - heißer Lead
 * 
 * 3. NA (Nicht Angetroffen):
 *    - Kann mit: BK, HOT
 *    - Entfernt: KT, KI, NO, WIN
 *    - Bedeutung: Kunde war nicht da, Follow-Up wird automatisch geplant
 * 
 * 4. KI (Kein Interesse) & NO (Nicht Optimierbar):
 *    - Können nur mit: BK
 *    - Entfernen: NA, KT, WIN, HOT
 *    - Bedeutung: Abschluss - keine weiteren Kontakte
 * 
 * 5. WIN (Gewonnen):
 *    - Kann mit: BK, KT
 *    - Entfernt: NA, KI, NO, HOT
 *    - Bedeutung: Erfolgreicher Abschluss - HOT Status wird automatisch entfernt
 * 
 * ========================================================================
 */

/**
 * Tags die mit ALLEN anderen Tags koexistieren können
 * NICHT ÄNDERN!
 */
export const UNIVERSAL_TAGS: D2DTag[] = ['BK', 'HOT'] as const;

/**
 * Ermittelt kompatible Tags beim Setzen eines neuen Tags
 * 
 * WICHTIG: Diese Funktion implementiert die oben definierten Regeln!
 * Gibt zurück welche bestehenden Tags behalten werden sollen.
 * 
 * @param currentTags - Aktuell gesetzte Tags
 * @param newTag - Neu zu setzendes Tag
 * @returns Array der kompatiblen Tags (inkl. dem neuen Tag)
 * 
 * NICHT ÄNDERN OHNE GENEHMIGUNG!
 */
export const getCompatibleTags = (currentTags: D2DTag[], newTag: D2DTag): D2DTag[] => {
  // ========================================================================
  // REGEL 1: BK und HOT - Universal Tags (toggle)
  // Ausnahme: HOT kann nicht mit KI oder NO koexistieren
  // ========================================================================
  if (newTag === 'BK' || newTag === 'HOT') {
    if (currentTags.includes(newTag)) {
      return currentTags.filter(t => t !== newTag);
    }
    
    // HOT kann nicht gesetzt werden wenn KI oder NO aktiv ist
    if (newTag === 'HOT' && currentTags.some(t => t === 'KI' || t === 'NO')) {
      return currentTags;
    }
    
    return [...currentTags, newTag];
  }

  // ========================================================================
  // REGEL 2: KT - Kontakt/Termin
  // Kann mit: BK, HOT
  // Entfernt: NA, KI, NO
  // Aktiviert automatisch: HOT
  // ========================================================================
  if (newTag === 'KT') {
    const compatibleTags = currentTags.filter(t => t === 'BK' || t === 'HOT');
    let result: D2DTag[] = [...compatibleTags, 'KT'];
    
    // HOT wird automatisch aktiviert wenn KT gesetzt wird
    if (!result.includes('HOT')) {
      result.push('HOT');
    }
    
    return result;
  }

  // ========================================================================
  // REGEL 3: NA - Nicht Angetroffen
  // Kann mit: BK, HOT
  // Entfernt: KT, KI, NO, WIN
  // ========================================================================
  if (newTag === 'NA') {
    // NA ist nicht "togglebar": erneuter Klick soll NA NICHT entfernen.
    if (currentTags.includes('NA')) {
      return [...currentTags];
    }
    const compatibleTags = currentTags.filter(t => t === 'BK' || t === 'HOT');
    return [...compatibleTags, 'NA'];
  }

  // ========================================================================
  // REGEL 4: KI und NO - Abschluss ohne Erfolg
  // Können nur mit: BK
  // Entfernen: NA, KT, WIN, HOT
  // ========================================================================
  if (newTag === 'KI' || newTag === 'NO') {
    if (currentTags.includes(newTag)) {
      return currentTags.filter(t => t !== newTag);
    }
    const compatibleTags = currentTags.filter(t => t === 'BK');
    return [...compatibleTags, newTag];
  }

  // ========================================================================
  // REGEL 5: WIN - Erfolgreicher Abschluss
  // Kann mit: BK, KT
  // Entfernt: NA, KI, NO, HOT
  // ========================================================================
  if (newTag === 'WIN') {
    if (currentTags.includes('WIN')) {
      return currentTags.filter(t => t !== 'WIN');
    }
    const compatibleTags = currentTags.filter(t => t === 'BK' || t === 'KT');
    return [...compatibleTags, 'WIN'];
  }

  // Default: just toggle the tag
  if (currentTags.includes(newTag)) {
    return currentTags.filter(t => t !== newTag);
  }
  return [...currentTags, newTag];
};

/**
 * Tags die mit KT koexistieren können
 * NICHT ÄNDERN!
 */
export const KT_COMPATIBLE_TAGS: D2DTag[] = ['BK', 'HOT'] as const;

/**
 * Tags die mit NA koexistieren können
 * NICHT ÄNDERN!
 */
export const NA_COMPATIBLE_TAGS: D2DTag[] = ['BK', 'HOT'] as const;

/**
 * Prüft ob ein Tag mit bestehenden Tags im Konflikt steht
 * 
 * @param currentTags - Aktuell gesetzte Tags
 * @param newTag - Neu zu setzendes Tag
 * @returns true wenn Konflikt besteht, sonst false
 * 
 * NICHT ÄNDERN!
 */
export const hasConflictingTag = (currentTags: D2DTag[], newTag: D2DTag): boolean => {
  if (newTag === 'KT') {
    return currentTags.some(t => t === 'NA' || t === 'KI' || t === 'NO');
  }
  if (newTag === 'NA') {
    return currentTags.some(t => t === 'KT' || t === 'KI' || t === 'NO' || t === 'WIN');
  }
  if (newTag === 'KI' || newTag === 'NO') {
    return currentTags.some(t => t === 'NA' || t === 'KT' || t === 'WIN' || t === 'HOT');
  }
  if (newTag === 'WIN') {
    return currentTags.some(t => t === 'NA' || t === 'KI' || t === 'NO' || t === 'HOT');
  }
  return false;
};
